import { useState, useMemo, useCallback } from "react";
import {
  doc,
  updateDoc,
  setDoc,
  addDoc,
  collection,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { EmptyState } from "@/components/ui/EmptyState";
import { WeekendCard } from "@/components/cards/WeekendCard";
import type { WeekendSuggestion, WeekendMoodVote } from "@/lib/models";
import { useHouseholdSubscription } from "@/hooks/useHouseholdSubscription";
import { LockedFeatureOverlay } from "@/components/features/LockedFeatureOverlay";
import { isFeatureUnlocked } from "@/lib/subscription";
import { useTranslations } from "@/contexts/I18nContext";
import { geocodeArea } from "@/lib/geocodeArea";

// `label` is also the stored Firestore value in `weekendMoodVotes.moods` —
// keep it as English so iOS / Android observe a consistent set. `labelKey`
// drives the translated display text only.
const MOOD_OPTIONS = [
  { label: "Outdoors", labelKey: "weekend.mood.outdoors", emoji: "\u{1F332}" },
  { label: "Chill at home", labelKey: "weekend.mood.chill", emoji: "\u{1F6CB}️" },
  { label: "Food & drinks", labelKey: "weekend.mood.food", emoji: "\u{1F37D}️" },
  { label: "Adventure", labelKey: "weekend.mood.adventure", emoji: "\u{1F3D4}️" },
  { label: "Arts & culture", labelKey: "weekend.mood.arts", emoji: "\u{1F3A8}" },
  { label: "Sports & active", labelKey: "weekend.mood.sports", emoji: "⚽" },
  { label: "Shopping", labelKey: "weekend.mood.shopping", emoji: "\u{1F6CD}️" },
  { label: "Surprise me", labelKey: "weekend.mood.surprise", emoji: "\u{1F3B2}" },
] as const;


function getThisSaturday(): Date {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  // On Sunday, show previous Saturday's weekend
  const offset = day === 0 ? -1 : 6 - day;
  const sat = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
  sat.setHours(0, 0, 0, 0);
  return sat;
}

function isSameWeekend(ts: Timestamp | undefined | null, saturday: Date): boolean {
  if (!ts) return false;
  const date =
    typeof (ts as { toDate?: () => Date }).toDate === "function"
      ? (ts as { toDate: () => Date }).toDate()
      : (ts as { seconds?: number }).seconds != null
        ? new Date((ts as { seconds: number }).seconds * 1000)
        : null;
  if (!date) return false;
  const satStart = new Date(saturday);
  satStart.setHours(0, 0, 0, 0);
  const sunEnd = new Date(satStart.getTime() + 2 * 24 * 60 * 60 * 1000);
  return date >= satStart && date < sunEnd;
}

export function WeekendPage() {
  const { householdId, household, profile, members, loading: ctxLoading } =
    useHouseholdContext();
  const t = useTranslations();
  const { state: subState } = useHouseholdSubscription();
  const weekendLocked = !isFeatureUnlocked("weekend_plans", subState);

  const householdHasArea = !!(
    household?.location_city ||
    household?.location_zip
  );

  const [areaCity, setAreaCity] = useState("");
  const [areaState, setAreaState] = useState("");
  const [areaZip, setAreaZip] = useState("");
  const [savingArea, setSavingArea] = useState(false);
  const [nudgeSent, setNudgeSent] = useState(false);

  const { data: allSuggestions, loading: suggestionsLoading } =
    useFirestoreCollection<WeekendSuggestion>(
      householdId ? `households/${householdId}/weekendSuggestions` : null
    );

  const { data: moodVotes } = useFirestoreCollection<WeekendMoodVote>(
    householdId ? `households/${householdId}/weekendMoodVotes` : null
  );

  const [selectedMoods, setSelectedMoods] = useState<Set<string>>(new Set());
  const [moodInitialized, setMoodInitialized] = useState(false);
  const [newIdea, setNewIdea] = useState("");

  const loading = ctxLoading || suggestionsLoading;
  // iOS treats `adult` and `elderly_parent` as adults for picking
  // weekend plans, sending nudges, and editing area.
  const isAdult =
    profile?.memberType === "adult" || profile?.memberType === "elderly_parent";
  const thisSaturday = useMemo(() => getThisSaturday(), []);

  // Initialize moods from existing vote
  const myMoodVote = useMemo(
    () => moodVotes.find((v) => v.uid === profile?.uid),
    [moodVotes, profile?.uid]
  );

  if (myMoodVote && !moodInitialized) {
    setSelectedMoods(new Set(myMoodVote.moods));
    setMoodInitialized(true);
  }

  // Filter to current weekend
  const weekendSuggestions = useMemo(() => {
    return allSuggestions
      .filter((s) => isSameWeekend(s.weekend_of, thisSaturday))
      .sort((a, b) => {
        // Chosen first
        if (a.is_chosen !== b.is_chosen) return a.is_chosen ? -1 : 1;
        // Then by thumbs up count
        const aUp = Object.values(a.votes ?? {}).filter((v) => v === true).length;
        const bUp = Object.values(b.votes ?? {}).filter((v) => v === true).length;
        return bUp - aUp;
      });
  }, [allSuggestions, thisSaturday]);

  const toggleMood = (mood: string) => {
    setSelectedMoods((prev) => {
      const next = new Set(prev);
      if (next.has(mood)) next.delete(mood);
      else next.add(mood);
      return next;
    });
  };

  const saveMoods = useCallback(async () => {
    if (!householdId || !profile?.uid) return;
    await setDoc(
      doc(db, `households/${householdId}/weekendMoodVotes`, profile.uid),
      {
        moods: Array.from(selectedMoods),
        voted_at: Timestamp.now(),
      }
    );
  }, [householdId, profile?.uid, selectedMoods]);

  const handleVote = useCallback(
    async (suggestion: WeekendSuggestion, isUpvote: boolean) => {
      if (!suggestion.uid || !householdId || !profile?.uid) return;
      const ref = doc(
        db,
        `households/${householdId}/weekendSuggestions`,
        suggestion.uid
      );
      const currentVote = suggestion.votes?.[profile.uid];
      if (currentVote === isUpvote) {
        // Remove vote
        const { [profile.uid]: _, ...rest } = suggestion.votes ?? {};
        await updateDoc(ref, { votes: rest });
      } else {
        await updateDoc(ref, { [`votes.${profile.uid}`]: isUpvote });
      }
    },
    [householdId, profile?.uid]
  );

  const handleChoose = useCallback(
    async (suggestion: WeekendSuggestion) => {
      if (!suggestion.uid || !householdId || !profile?.uid || !isAdult) return;
      // Clear other choices for this weekend
      for (const s of weekendSuggestions) {
        if (s.uid && s.is_chosen && s.uid !== suggestion.uid) {
          await updateDoc(
            doc(db, `households/${householdId}/weekendSuggestions`, s.uid),
            { is_chosen: false, chosen_by: null }
          );
        }
      }
      // Toggle this one
      const ref = doc(
        db,
        `households/${householdId}/weekendSuggestions`,
        suggestion.uid
      );
      await updateDoc(ref, {
        is_chosen: !suggestion.is_chosen,
        chosen_by: suggestion.is_chosen ? null : profile.uid,
      });
    },
    [householdId, profile?.uid, isAdult, weekendSuggestions]
  );

  const handleAddCustom = useCallback(async () => {
    const title = newIdea.trim();
    if (!title || !householdId || !profile?.uid) return;
    await addDoc(
      collection(db, `households/${householdId}/weekendSuggestions`),
      {
        title,
        tier: "local",
        source: "custom",
        weekend_of: Timestamp.fromDate(thisSaturday),
        created_at: Timestamp.now(),
        votes: { [profile.uid]: true },
      }
    );
    setNewIdea("");
  }, [newIdea, householdId, profile?.uid, thisSaturday]);

  const findMember = (uid: string) => members.find((m) => m.uid === uid);

  const handleSaveArea = useCallback(async () => {
    if (!householdId) return;
    const city = areaCity.trim();
    const state = areaState.trim();
    const zip = areaZip.trim();
    if (!city && !zip) return;
    setSavingArea(true);
    try {
      // Geocode in parallel with the primary write — weekend
      // suggestions rank by lat/lng radius
      // (`functions/src/weekend.ts:215`), so dropping these would
      // silently break ranking. Failure resolves to null and we
      // simply skip the lat/lng write.
      const geocodePromise = geocodeArea({ city, state, zip });
      await updateDoc(doc(db, "households", householdId), {
        location_city: city || null,
        location_state: state || null,
        location_zip: zip || null,
      });
      const coords = await geocodePromise;
      if (coords) {
        await updateDoc(doc(db, "households", householdId), {
          location_lat: coords.lat,
          location_lng: coords.lng,
        });
      }
    } catch (err) {
      console.error("Failed to save area:", err);
    } finally {
      setSavingArea(false);
    }
  }, [householdId, areaCity, areaState, areaZip]);

  const handleSendAreaNudge = useCallback(async () => {
    if (!householdId) return;
    try {
      const functions = getFunctions();
      const nudge = httpsCallable<
        { householdId: string; childName?: string },
        { ok?: boolean }
      >(functions, "sendAreaNudge");
      await nudge({
        householdId,
        childName: profile?.nickname ?? profile?.name,
      });
      setNudgeSent(true);
      setTimeout(() => setNudgeSent(false), 5000);
    } catch (err) {
      console.error("Failed to send area nudge:", err);
    }
  }, [householdId, profile?.nickname, profile?.name]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <LockedFeatureOverlay
      locked={weekendLocked}
      title="Weekend plans"
      description="AI-curated weekend ideas (with weather + packing tips) are a Zoey+ feature."
      paywallTitle="Unlock weekend plans"
      paywallSubtitle="Zoey+ suggests local activities, day trips, and getaways tailored to your household's moods."
    >
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-bold">{t("weekend.title")}</h1>

      {/* Area gating — adult sees AreaPicker, child sees nudge button */}
      {!householdHasArea && (
        <Card>
          {isAdult ? (
            <div className="space-y-2">
              <p className="font-semibold text-sm">{t("weekend.area.adult.title")}</p>
              <p className="text-xs text-gray-500">
                {t("weekend.area.adult.body")}
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  value={areaCity}
                  onChange={(e) => setAreaCity(e.target.value)}
                  placeholder={t("weekend.area.city")}
                  className="flex-1 min-w-[8rem] rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                />
                <input
                  type="text"
                  value={areaState}
                  onChange={(e) => setAreaState(e.target.value)}
                  placeholder={t("weekend.area.state")}
                  className="w-24 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                />
                <input
                  type="text"
                  value={areaZip}
                  onChange={(e) => setAreaZip(e.target.value)}
                  placeholder={t("weekend.area.zip")}
                  className="w-28 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                />
              </div>
              <button
                type="button"
                onClick={handleSaveArea}
                disabled={savingArea || (!areaCity.trim() && !areaZip.trim())}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
              >
                {savingArea ? t("weekend.area.saving") : t("weekend.area.save")}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="font-semibold text-sm">{t("weekend.area.child.title")}</p>
              <p className="text-xs text-gray-500">
                {t("weekend.area.child.body")}
              </p>
              <button
                type="button"
                onClick={handleSendAreaNudge}
                disabled={nudgeSent}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-60 transition-opacity"
              >
                {nudgeSent ? t("weekend.area.child.sent") : t("weekend.area.child.cta")}
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Mood Selector */}
      <section>
        <SectionHeader
          title={myMoodVote ? t("weekend.mood.titleAfterVote") : t("weekend.mood.titleBeforeVote")}
          icon="🎭"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {MOOD_OPTIONS.map((mood) => {
            const active = selectedMoods.has(mood.label);
            return (
              <button
                key={mood.label}
                onClick={() => toggleMood(mood.label)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? "bg-[color:var(--accent-color)] text-white"
                    : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15"
                }`}
              >
                {mood.emoji} {t(mood.labelKey)}
              </button>
            );
          })}
        </div>
        <button
          onClick={saveMoods}
          disabled={selectedMoods.size === 0}
          className="mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
        >
          {myMoodVote ? t("weekend.mood.update") : t("weekend.mood.save")}
        </button>
      </section>

      {/* Add Custom Idea */}
      <section>
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddCustom();
            }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={newIdea}
              onChange={(e) => setNewIdea(e.target.value)}
              placeholder={t("weekend.idea.placeholder")}
              className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
            <button
              type="submit"
              disabled={!newIdea.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
            >
              {t("weekend.idea.save")}
            </button>
          </form>
        </Card>
      </section>

      {/* Suggestions */}
      <section>
        <SectionHeader
          title={t("weekend.suggestions.title")}
          count={weekendSuggestions.length}
          icon="🌤️"
        />
        {weekendSuggestions.length === 0 ? (
          <Card className="mt-2">
            <EmptyState
              icon="\u{1F3D5}️"
              title={t("weekend.suggestions.emptyTitle")}
              subtitle={t("weekend.suggestions.emptySubtitle")}
            />
          </Card>
        ) : (
          <div className="mt-2 space-y-3">
            {weekendSuggestions.map((s) => (
              <WeekendCard
                key={s.uid}
                suggestion={s}
                userId={profile?.uid}
                onVote={handleVote}
                variant="full"
                isAdult={isAdult}
                onChoose={handleChoose}
                findMember={findMember}
              />
            ))}
          </div>
        )}
      </section>
    </div>
    </LockedFeatureOverlay>
  );
}
